import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, BookOpen, Camera, Send, CheckCircle, ArrowRight, Mail, Megaphone } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title="About Us"
        description="Meet the team behind I DO Bridal Magazine Malta. Learn about our editorial approach, submission process, and how we celebrate Maltese love stories."
      />
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&h=800&fit=crop"
            alt="About I DO Bridal Magazine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FAF9F7]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-white/90 text-xs tracking-[0.3em] uppercase mb-4">ABOUT US</p>
              <h1 
                className="text-4xl md:text-6xl text-white mb-4 tracking-tight drop-shadow-lg"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Celebrating Love Stories
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 
            className="text-3xl md:text-4xl text-[#2C2C2C] mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            I DO Bridal Magazine
          </h2>
          <div className="w-16 h-0.5 mx-auto mb-8" style={{ backgroundColor: '#DE92A9' }}></div>
          <p className="text-base leading-relaxed mb-5 text-[#757B7F]">
            <strong className="text-[#DE92A9]">I DO</strong> is Malta's modern bridal magazine, dedicated to celebrating love, commitment, and the stories behind unforgettable wedding days.
          </p>
          <p className="text-base leading-relaxed mb-5 text-[#757B7F]">
            Proudly part of <strong className="text-[#2C2C2C]">Independent Malta</strong>, I DO was created to offer couples and industry professionals a refined, inspiring, and trusted space where real weddings, meaningful details, and creative talent come together.
          </p>
          <p className="text-base leading-relaxed text-[#757B7F] italic border-l-4 border-[#DE92A9] pl-6 text-left max-w-2xl mx-auto mt-8">
            We believe every love story deserves to be told beautifully and authentically.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-base md:text-lg mb-4 italic" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>
              Our Focus
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#2C2C2C] tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
              What We Do
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Heart, title: 'Real Wedding Stories', desc: 'From couples across Malta and beyond, honest, heartfelt stories that celebrate every unique love journey.' },
              { icon: BookOpen, title: 'Editorial Features', desc: 'Inspiration covering fashion, beauty, planning, d\u00e9cor, and venues to guide every step of your wedding journey.' },
              { icon: Camera, title: 'Styled Shoots', desc: 'Creative collaborations with leading and emerging wedding professionals that push the boundaries of beauty.' },
              { icon: Users, title: 'Curated Vendor Guide', desc: 'Connecting couples with trusted suppliers, carefully selected professionals you can rely on.' },
            ].map((card) => (
              <div key={card.title} className="p-7 rounded-xl border border-[#E5E0DA] bg-[#FDFCFB]">
                <div className="w-11 h-11 rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: '#DE92A9' }}>
                  <card.icon size={18} className="text-white" />
                </div>
                <h3 className="text-lg mb-2" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>{card.title}</h3>
                <p className="text-sm leading-relaxed text-[#757B7F]">{card.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[#757B7F] max-w-3xl mx-auto">
            Our focus is on storytelling, strong visuals, and thoughtful content that reflects both timeless elegance and modern romance.
          </p>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20" data-testid="about-how-we-work">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-base md:text-lg mb-4 italic" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>
              Simple & transparent
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>
              How We Work
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Send, step: '01', title: 'Submit', desc: 'Send us your wedding story, styled shoot, or vendor collaboration.' },
              { icon: CheckCircle, step: '02', title: 'Review', desc: 'Our editorial team reviews every submission for quality and fit.' },
              { icon: BookOpen, step: '03', title: 'Feature', desc: 'Selected stories are published in print and online for our community.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#DE92A9' }}>
                  <item.icon size={22} className="text-white" />
                </div>
                <span className="text-xs tracking-[0.2em] font-semibold block mb-2" style={{ color: '#DE92A9' }}>
                  STEP {item.step}
                </span>
                <h3 className="text-xl mb-3" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>{item.title}</h3>
                <p className="text-sm leading-relaxed text-[#757B7F]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl tracking-tight" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>
              Our Editorial Approach
            </h2>
          </div>
          <div className="space-y-6 text-center">
            <p className="text-lg leading-relaxed" style={{ color: '#4A4A4A' }}>
              We value <strong>authenticity over perfection</strong> and <strong>storytelling over trends</strong>.
            </p>
            <p className="text-base leading-relaxed text-[#757B7F]">
              Every feature is carefully reviewed by our editorial team to ensure it aligns with our aesthetic, values, and quality standards.
            </p>
            <p className="text-base leading-relaxed text-[#757B7F]">
              By working closely with couples, photographers, and wedding professionals, we aim to showcase weddings that feel personal, intentional, and genuinely reflective of the people behind them.
            </p>
          </div>
        </div>
      </section>

      {/* Supporting Malta */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>
            Supporting Malta's Wedding Industry
          </h2>
          <p className="text-base leading-relaxed mb-5 text-[#757B7F]">
            As part of Independent Malta, <strong style={{ color: '#DE92A9' }}>I DO</strong> also serves as a platform that supports and uplifts the local wedding industry.
          </p>
          <p className="text-base leading-relaxed text-[#757B7F]">
            Through features, collaborations, and advertising opportunities, we help vendors share their work with a wider audience while maintaining editorial integrity and trust.
          </p>
        </div>
      </section>

      {/* Our Community */}
      <section className="py-20 bg-white" data-testid="about-community">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-base md:text-lg mb-4 italic" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>
              Join us
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>
              Our Community
            </h2>
          </div>
          <p className="text-base md:text-lg text-center leading-relaxed max-w-3xl mx-auto mb-14" style={{ color: '#4A4A4A' }}>
            I DO is more than a magazine, it's a growing community of couples, creatives, and professionals who share a love for beautiful celebrations and meaningful moments. We are here to inspire, inform, and celebrate alongside you.
          </p>

          <div className="text-center mb-12">
            <h3 className="text-xl tracking-wide mb-8" style={{ color: '#2C2C2C', letterSpacing: '0.1em' }}>WE WELCOME</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {[
                { label: 'Wedding Story Submissions', link: '/submit-wedding', icon: Heart },
                { label: 'Vendor Collaborations', link: '/contact', icon: Users },
                { label: 'Advertising Enquiries', link: '/advertise', icon: Megaphone },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.link}
                  className="group flex items-center justify-center gap-3 px-6 py-5 rounded-lg border border-[#E5E0DA] transition-all duration-300 hover:border-[#DE92A9]/30 hover:shadow-sm bg-[#FDFCFB]"
                >
                  <item.icon size={16} style={{ color: '#DE92A9' }} />
                  <span className="text-sm font-medium group-hover:text-[#DE92A9] transition-colors text-[#2C2C2C]">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/contact" className="inline-flex items-center gap-2 text-sm tracking-[0.15em] font-semibold px-8 py-4 rounded-sm transition-all duration-300 hover:opacity-90" style={{ backgroundColor: '#DE92A9', color: '#fff' }}>
              <Mail size={14} />
              CONTACT US
            </Link>
            <Link to="/advertise" className="inline-flex items-center gap-2 text-sm tracking-[0.15em] font-semibold px-8 py-4 border-2 transition-all duration-300 hover:gap-3 group" style={{ borderColor: '#DE92A9', color: '#DE92A9' }}>
              ADVERTISE WITH US
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Issues */}
      <section className="py-20" data-testid="about-issues">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-base md:text-lg mb-4 italic" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>
              In print & coming soon
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>
              Our Issues
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-xl overflow-hidden border border-[#E5E0DA] p-10 flex flex-col justify-between bg-[#FDFCFB]" style={{ minHeight: '300px' }}>
              <div>
                <span className="inline-block text-[10px] tracking-[0.15em] font-semibold px-3 py-1.5 rounded-sm mb-6" style={{ backgroundColor: '#DE92A9', color: '#fff' }}>OUT NOW</span>
                <h3 className="text-2xl md:text-3xl mb-3" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>Spring / Summer 2026</h3>
                <p className="text-lg mb-2" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>Candyland Romance</p>
                <p className="text-sm leading-relaxed mt-4 text-[#757B7F]">Issue No 95, featuring real wedding stories, the new era of bridesmaids, soft romantic beauty trends, and Malta's finest wedding vendors. Available in print and digital.</p>
              </div>
              <Link to="/the-magazine" className="inline-flex items-center gap-2 text-sm font-semibold mt-6 group" style={{ color: '#DE92A9' }}>
                VIEW ISSUE <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="rounded-xl overflow-hidden border border-[#E5E0DA] p-10 flex flex-col justify-between bg-[#FDFCFB]" style={{ minHeight: '300px' }}>
              <div>
                <span className="inline-block text-[10px] tracking-[0.15em] font-semibold px-3 py-1.5 rounded-sm mb-6" style={{ backgroundColor: '#2C2C2C', color: '#fff' }}>COMING SOON</span>
                <h3 className="text-2xl md:text-3xl mb-3" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>Winter 2026</h3>
                <p className="text-lg mb-2" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>To Be Revealed</p>
                <p className="text-sm leading-relaxed mt-4 text-[#757B7F]">Cosy candlelight, rich textures, and festive-season celebrations. Our winter edition is already in the works, featuring real weddings and styled shoots from Malta and Gozo.</p>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold mt-6 group" style={{ color: '#DE92A9' }}>
                SUBMIT FOR THIS ISSUE <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
